import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import OrderState from './types'
import EClientType from '@/utils/enums/client-type.enum'
import BRAND_NAME from '@/utils/constants/brand.constant'
import { IPlanWithExtraParams } from '@/types/plan.interface'
import { EPeriod } from '@/utils/enums/period.enum'
import reachGoalMetrika from '@/utils/helpers/reach-goal-metrika.helper'
import { IHMSDedicatedServer, IHMSDedicatedServerCartItem } from '@/types/dedicated.interface'

const initialState: OrderState = {
  clientType: EClientType.INDIVIDUAL,
  agreement: false,
  brand: BRAND_NAME,
  brandId: BRAND_NAME,
  cart: {
    domains: [],
    services: {},
    hostingPeriod: EPeriod.P1M,
    dedicatedServers: [],
  },
}

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setClientType(state, action: PayloadAction<EClientType>) {
      state.clientType = action.payload
    },
    setAgreement(state, action: PayloadAction<boolean>) {
      state.agreement = action.payload
    },
    setPromocode(state, action: PayloadAction<string>) {
      state.promocode = action.payload.toUpperCase()
    },
    addDomainToCart(state, action: PayloadAction<{ name: string; price: number }>) {
      const domainItem = state.cart.domains.find((item) => item.name === action.payload.name)
      if (!domainItem) {
        state.cart.domains.push({
          name: action.payload.name,
          accountPromotionId: null,
          accountPromotionName: null,
          autoRenew: false,
          personId: '',
          price: action.payload.price,
          processing: false,
          type: 'domain',
        })

        reachGoalMetrika('add_domain_to_cart')
      }
    },
    removeDomainFromCart(state, action: PayloadAction<string>) {
      state.cart.domains = state.cart.domains.filter((domain) => domain.name !== action.payload)
    },
    setPlan(state, action: PayloadAction<IPlanWithExtraParams>) {
      state.cart.hostingPlan = action.payload

      if (!state.cart.hostingPeriod) {
        state.cart.hostingPeriod = state.cart.hostingPlan.extraParams.periods[0]
      }

      const selectedPeriodExistsInPlan = state.cart.hostingPlan.extraParams.periods.find(
        (period) => period === state.cart.hostingPeriod
      )
      if (!selectedPeriodExistsInPlan) {
        state.cart.hostingPeriod = state.cart.hostingPlan.extraParams.periods[0]
      }

      if (state.cart.hostingPlan) {
        reachGoalMetrika('hosting_plan_selected')
      }
    },
    removePlan(state) {
      state.cart.hostingPlan = undefined
      state.cart.hostingPeriod = EPeriod.P1M
    },
    setPeriod(state, action: PayloadAction<EPeriod>) {
      if (!state.cart.hostingPlan) return

      const selectedPeriodExistsInPlan = state.cart.hostingPlan.extraParams.periods.find(
        (period) => period === action.payload
      )
      if (selectedPeriodExistsInPlan) {
        state.cart.hostingPeriod = action.payload
      }
    },
    addDedicatedServer(state, action: PayloadAction<IHMSDedicatedServerCartItem>) {
      if (state.cart.dedicatedServers?.find((server) => server.plan.id === action.payload.plan.id)) return

      state.cart.dedicatedServers = [...(state.cart.dedicatedServers || []), {plan: action.payload.plan, period: action.payload.period, comment: action.payload.comment}]

      reachGoalMetrika('dedicated_server_added')
    },
    removeDedicatedServer(state, action: PayloadAction<string>) {
      state.cart.dedicatedServers = state.cart.dedicatedServers?.filter((server) => server.plan.id !== action.payload)
    },
    setConfirmationCode(state, action: PayloadAction<string>) {
      state.confirmationCode = action.payload
    },
    clearCart(state) {
      state.cart = {
        domains: [],
        services: {},
        hostingPeriod: EPeriod.P1M,
        dedicatedServers: [],
      }
    },
  },
})

export const {
  addDomainToCart,
  removeDomainFromCart,
  setClientType,
  setAgreement,
  setPeriod,
  setPlan,
  setPromocode,
  removePlan,
  setConfirmationCode,
  clearCart,
  addDedicatedServer,
  removeDedicatedServer,
} = orderSlice.actions

export default orderSlice
